import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { CalendarDays, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MaskedLines, Reveal } from "../components/Reveal";
import { CONCERNS, TIME_SLOTS } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputBase =
  "w-full bg-transparent border-b border-[#2C3C30]/25 focus:border-[#2C3C30] outline-none py-3 font-sans text-lg text-[#1A211D] placeholder:text-[#7D8C7A] transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    concern: "",
    message: "",
  });
  const [date, setDate] = useState(null);
  const [timeSlot, setTimeSlot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.concern) {
      toast.error("Please fill in your name, email, phone and concern.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        preferred_date: date ? format(date, "yyyy-MM-dd") : null,
        preferred_time: timeSlot || null,
      });
      setDone(true);
      toast.success("Consultation request received. We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", concern: "", message: "" });
      setDate(null);
      setTimeSlot("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-28 md:pt-36 pb-24 md:pb-40">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10">
        <p className="font-sans text-xs tracking-[0.4em] uppercase text-[#7D8C7A] mb-8">
          Contact & Booking
        </p>
        <h1 className="font-serif text-[#2C3C30] text-6xl md:text-8xl lg:text-[8vw] leading-[0.9] tracking-tighter">
          <MaskedLines
            lines={["Let's begin", <em key="e" className="text-[#7D8C7A]">your healing.</em>]}
          />
        </h1>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
        <div className="md:col-span-4">
          <Reveal>
            <p className="font-serif text-3xl text-[#2C3C30] tracking-tight leading-snug">
              Book a clinical consultation and take the first step toward a
              calmer, healthier gut.
            </p>
            <div className="mt-10 space-y-6 font-sans text-[#566158]">
              <p>
                Share a little about your concern and preferred timing. We&rsquo;ll
                call or text you to confirm your appointment and next steps.
              </p>
              <div className="h-px w-full bg-[#2C3C30]/15" />
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#7D8C7A] mb-2">
                  Direct
                </p>
                <p className="text-[#2C3C30]">helloadya4@gmail.com</p>
                <p className="text-[#2C3C30]">+91 81082 73005</p>
                <p className="text-[#2C3C30]">Mumbai, India &middot; Clinic &amp; Telehealth</p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-7 md:col-start-6">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#2C3C30] text-[#F7F6F2] rounded-3xl p-10 md:p-14"
              data-testid="booking-success"
            >
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-[#7D8C7A] mb-6">
                <Check size={28} className="text-[#F7F6F2]" />
              </div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-4">
                Request received.
              </h2>
              <p className="font-sans text-[#D1D6CC] leading-relaxed max-w-md">
                Thank you for reaching out. We&rsquo;ll review your details and
                confirm your consultation shortly. Keep an eye on your inbox.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="booking-another-btn"
                className="mt-8 font-sans text-sm uppercase tracking-wide border-b border-[#F7F6F2]/50 pb-1 hover:border-[#F7F6F2] transition-colors"
              >
                Submit another request
              </button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="booking-form" className="space-y-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158]">
                    Full Name
                  </label>
                  <input
                    className={inputBase}
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158]">
                    Email
                  </label>
                  <input
                    type="email"
                    className={inputBase}
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158]">
                    Phone
                  </label>
                  <input
                    className={inputBase}
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+91 ..."
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158] block mb-2">
                    Primary Concern
                  </label>
                  <Select
                    value={form.concern}
                    onValueChange={(v) => setForm((f) => ({ ...f, concern: v }))}
                  >
                    <SelectTrigger
                      className="border-0 border-b border-[#2C3C30]/25 rounded-none px-0 h-auto py-3 text-lg shadow-none focus:ring-0 text-[#1A211D]"
                      data-testid="select-concern"
                    >
                      <SelectValue placeholder="Select a concern" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONCERNS.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`concern-${c}`}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158] block mb-2">
                    Preferred Date
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="date-trigger"
                        className="w-full flex items-center justify-between border-b border-[#2C3C30]/25 py-3 font-sans text-lg text-left hover:border-[#2C3C30] transition-colors"
                      >
                        <span className={date ? "text-[#1A211D]" : "text-[#7D8C7A]"}>
                          {date ? format(date, "EEE, dd MMM yyyy") : "Pick a date"}
                        </span>
                        <CalendarDays size={18} className="text-[#7D8C7A]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158] block mb-2">
                    Preferred Time
                  </label>
                  <Select value={timeSlot} onValueChange={setTimeSlot}>
                    <SelectTrigger
                      className="border-0 border-b border-[#2C3C30]/25 rounded-none px-0 h-auto py-3 text-lg shadow-none focus:ring-0 text-[#1A211D]"
                      data-testid="select-time"
                    >
                      <SelectValue placeholder="Pick a time slot" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`time-${t}`}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158]">
                  Tell us more (optional)
                </label>
                <textarea
                  rows={3}
                  className={`${inputBase} resize-none`}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Symptoms, history, questions..."
                  data-testid="input-message"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-booking-btn"
                className="group inline-flex items-center gap-3 rounded-full bg-[#2C3C30] px-10 py-4 text-[#F7F6F2] font-medium transition-colors duration-300 hover:bg-[#1A211D] disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending...
                  </>
                ) : (
                  "Request Consultation"
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
